"use client";

import { adminNoticePageState } from "@/atoms/notice/admin-notice-page-state";
import { toastState } from "@/atoms/system/toast-state";
import { ButtonColor } from "@/common/enum/button-color";
import { noticeService } from "@/services/notice-service";
import { NoticeDetailDto } from "@/types/dto/notice-detail-dto";
import { Button, Label, Modal, Radio, TextInput } from "flowbite-react";
import React, { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { CustomDatepicker } from "../datepicker/CustomDatepicker";
import { TextInputLabel } from "../label/TextInputLabel";
import { CustomEditor } from "../tiptap/CustomEditor";
import { Body14Regular } from "../typography/Body14Regular";
import moment from "moment";

interface Props {
  selectedNotice: NoticeDetailDto;
  clearSelectedNotice: () => void;
  handleDeleteNoticeModalOpen: () => void;
}

const todayStart = moment().startOf("day").toDate();

export const UpdateAdminNoticeModal: React.FC<Props> = ({
  selectedNotice,
  clearSelectedNotice,
  handleDeleteNoticeModalOpen,
}) => {
  const setNoticePage = useSetRecoilState(adminNoticePageState);
  const setToast = useSetRecoilState(toastState);
  const [updateNoticePayload, setUpdateNoticePayload] = useState({
    ...selectedNotice,
    content: selectedNotice.content || "{}",
    popupStartDate: selectedNotice.popupStartDate
      ? new Date(selectedNotice.popupStartDate)
      : undefined,
    popupEndDate: selectedNotice.popupEndDate
      ? new Date(selectedNotice.popupEndDate)
      : undefined,
  });
  const [isCataloged, setIsCataloged] = useState(
    !!selectedNotice.popupStartDate
  );
  const isActive =
    !!updateNoticePayload.title &&
    updateNoticePayload.content !== "{}" &&
    updateNoticePayload.content !==
      '{"type":"doc","content":[{"type":"paragraph"}]}';

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setUpdateNoticePayload({
      ...updateNoticePayload,
      title: value,
    });
  };

  const handleContentChange = (content: string) => {
    setUpdateNoticePayload({
      ...updateNoticePayload,
      content,
    });
  };

  const updateNoticePageById = (newNotice: NoticeDetailDto) => {
    setNoticePage((prev) => ({
      ...prev,
      contents: prev.contents.map((notice) =>
        notice.id === newNotice.id ? newNotice : notice
      ),
    }));
  };

  const handleUpdateNoticeButtonClick = async () => {
    try {
      const updateReponse = await noticeService.update(
        selectedNotice.id,
        updateNoticePayload
      );
      if (!updateReponse.ok) {
        return;
      }
      const getByIdResponse = await noticeService.getById({
        id: selectedNotice.id,
      });
      if (!getByIdResponse.ok) {
        return;
      }
      setToast({
        show: true,
        messages: [`공지사항을 등록했습니다.`],
      });
      updateNoticePageById(getByIdResponse.data);
      clearSelectedNotice();
    } catch (error) {
      console.log(error);
    }
  };

  const handlePopupStartDateChange = (date: Date) => {
    if (
      updateNoticePayload.popupEndDate &&
      updateNoticePayload.popupEndDate < date
    ) {
      console.log("시작일은 종료일보다 클 수 없습니다.");
      return;
    }

    setUpdateNoticePayload({
      ...updateNoticePayload,
      popupStartDate: date,
    });
  };

  const handlePopupEndDateChange = (date: Date) => {
    if (
      updateNoticePayload.popupStartDate &&
      updateNoticePayload.popupStartDate > date
    ) {
      console.log("시작일은 종료일보다 클 수 없습니다.");
      return;
    }

    setUpdateNoticePayload({
      ...updateNoticePayload,
      popupEndDate: date,
    });
  };

  const handleDeleteNoticeButtonClick = () => {
    handleDeleteNoticeModalOpen();
  };

  const clearPopupDate = () => {
    setUpdateNoticePayload({
      ...updateNoticePayload,
      popupStartDate: undefined,
      popupEndDate: undefined,
    });
  };

  useEffect(() => {
    if (isCataloged) {
      setUpdateNoticePayload((prev) => ({
        ...prev,
        popupStartDate: prev.popupStartDate ?? todayStart,
        popupEndDate: prev.popupEndDate ?? todayStart,
      }));
    } else {
      clearPopupDate();
    }
  }, [isCataloged]);

  return (
    <Modal size="2xl" show={true} onClose={clearSelectedNotice}>
      <Modal.Header>공지사항 수정</Modal.Header>
      <Modal.Body>
        <div className="flex h-[430px] flex-col">
          <TextInputLabel required>제목</TextInputLabel>
          <TextInput
            required
            className="w-full mb-[20px]"
            placeholder="제목을 입력해주세요"
            name="title"
            value={updateNoticePayload.title}
            onChange={handleTitleChange}
          />
          <TextInputLabel required>팝업 노출</TextInputLabel>
          <fieldset className="flex max-w-md gap-4 mb-[8px]">
            <div className="flex items-center gap-2">
              <Radio
                id="catalog-no"
                name="catalog"
                value="NO"
                checked={!isCataloged}
                onChange={() => setIsCataloged(false)}
              />
              <Label htmlFor="catalog-no">노출 안함</Label>
            </div>
            <div className="flex items-center gap-2">
              <Radio
                id="catalog-yes"
                name="catalog"
                value="YES"
                checked={isCataloged}
                onChange={() => setIsCataloged(true)}
              />
              <Label htmlFor="catalog-yes">노출함</Label>
            </div>
          </fieldset>
          <Body14Regular className="text-semantic-text-tertiary">
            해당 기간동안 접속한 사용자들에게 팝업으로 노출됩니다.
          </Body14Regular>
          {isCataloged && (
            <div className="flex gap-[8px] mt-[16px]">
              <div className="flex flex-col w-full">
                <TextInputLabel
                  required
                  className="mb-[4px] text-semantic-text-heading"
                >
                  시작일
                </TextInputLabel>
                <CustomDatepicker
                  selectedDate={updateNoticePayload.popupStartDate}
                  maxDate={updateNoticePayload.popupEndDate}
                  onDateChange={handlePopupStartDateChange}
                />
              </div>
              <div className="flex flex-col w-full">
                <TextInputLabel
                  required
                  className="mb-[4px] text-semantic-text-heading"
                >
                  종료일
                </TextInputLabel>
                <CustomDatepicker
                  selectedDate={updateNoticePayload.popupEndDate}
                  minDate={updateNoticePayload.popupStartDate}
                  onDateChange={handlePopupEndDateChange}
                />
              </div>
            </div>
          )}
          <div className="mt-[20px] mb-[4px] text-semantic-text-heading">
            <TextInputLabel required>내용</TextInputLabel>
          </div>
          <div className="pb-[24px]">
            <CustomEditor
              content={updateNoticePayload.content}
              handleContentChange={handleContentChange}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button color={ButtonColor.RED} onClick={handleDeleteNoticeButtonClick}>
          삭제하기
        </Button>
        <Button
          color={isActive ? ButtonColor.PRIMARY : ""}
          onClick={handleUpdateNoticeButtonClick}
          disabled={!isActive}
        >
          수정하기
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
